import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  createUserWithEmailAndPassword,
  onIdTokenChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  User,
  UserCredential,
} from "firebase/auth";
import * as Sentry from "@sentry/react";

import { API_BASE_URL } from "@/config";
import { initialAuthLoadingDelay } from "@/lib/authLoadingDelay";
import { fetchVerifiedProfile, isSessionInvalidError } from "@/lib/authSession";
import { clearVpcApiKey, getVpcApiKey, hasVpcApiKey, isVpcMode, setVpcApiKey } from "@/utils/vpcMode";
import {
  AUTH_NOTICE_UPDATED_EVENT,
  ACCOUNT_STATE_CHECK_FAILED_MESSAGE,
  ACCOUNT_STATE_CHECK_FAILED_NOTICE_KEY,
  PASSWORD_EXPIRED_MESSAGE,
  PASSWORD_EXPIRED_NOTICE_KEY,
  SESSION_EXPIRED_MESSAGE,
  SESSION_EXPIRED_NOTICE_KEY,
} from "@/constants/auth";
import AppLoadingScreen from "../components/AppLoadingScreen";
import { auth } from "../firebase";

type AuthError = Error & {
  code?: string;
  detail?: string;
  status?: number;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  signup: (
    email: string,
    password: string,
    verificationReturnTo?: string,
  ) => Promise<UserCredential>;
  sendVerificationEmail: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  getToken: (refresh?: boolean) => Promise<string | null>;
  validateAuthenticatedUser: (firebaseUser: User) => Promise<void>;
};

type CurrentUserProfile = {
  password_reset_required?: boolean;
  uid?: string;
  email?: string | null;
};

type LoginAccountState = {
  password_reset_required?: boolean;
};

const SESSION_VALIDATION_INTERVAL_MS = 60_000;

const AuthContext = createContext<AuthContextType | null>(null);

const buildAuthError = (code: string, message: string): AuthError => {
  const error = new Error(message) as AuthError;
  error.code = code;
  return error;
};

const storePasswordExpiredNotice = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(PASSWORD_EXPIRED_NOTICE_KEY, "true");
};

const clearPasswordExpiredNotice = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(PASSWORD_EXPIRED_NOTICE_KEY);
};

const storeSessionExpiredNotice = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(SESSION_EXPIRED_NOTICE_KEY, "true");
};

const clearSessionExpiredNotice = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(SESSION_EXPIRED_NOTICE_KEY);
};

const storeAccountStateCheckFailedNotice = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(ACCOUNT_STATE_CHECK_FAILED_NOTICE_KEY, "true");
};

const clearAccountStateCheckFailedNotice = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(ACCOUNT_STATE_CHECK_FAILED_NOTICE_KEY);
};

const clearAuthNotices = () => {
  clearPasswordExpiredNotice();
  clearSessionExpiredNotice();
  clearAccountStateCheckFailedNotice();
};

const emitAuthNoticeUpdated = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_NOTICE_UPDATED_EVENT));
};

const redirectToLoginForPasswordReset = () => {
  if (typeof window === "undefined") return;
  clearAuthNotices();
  storePasswordExpiredNotice();
  emitAuthNoticeUpdated();
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

const redirectToLoginForSessionExpiry = () => {
  if (typeof window === "undefined") return;
  clearAuthNotices();
  storeSessionExpiredNotice();
  emitAuthNoticeUpdated();
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

const redirectToLoginForAccountStateCheckFailure = () => {
  if (typeof window === "undefined") return;
  clearAuthNotices();
  storeAccountStateCheckFailedNotice();
  emitAuthNoticeUpdated();
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

const signOutQuietly = async () => {
  try {
    await signOut(auth);
  } catch (signOutError) {
    console.error("Error signing out:", signOutError);
  }
};

const fetchCurrentUserProfile = async (token: string): Promise<CurrentUserProfile> => {
  const response = await fetch(`${API_BASE_URL}/users/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    const detail =
      typeof errorPayload?.detail === "string"
        ? errorPayload.detail
        : `Failed to fetch current user profile (${response.status})`;
    const error = new Error(detail) as AuthError;
    error.status = response.status;
    error.detail = detail;
    throw error;
  }

  return await response.json();
};

/**
 * VPC mode: Firebase is stubbed and the api_key (URL -> localStorage) is the
 * identity, so there is nothing to log in with. Shape the caller returned by
 * GET /users/me as a firebase User so every `user`-gated code path (sidebar,
 * CreditContext profile, "please sign in" checks, user.uid scopes) behaves as
 * signed in. getIdToken hands out the api key; main.tsx's fetch patch turns the
 * resulting Authorization header into X-API-Key.
 */
const buildVpcUser = (apiKey: string, profile: CurrentUserProfile | null): User => {
  const uid = profile?.uid || "vpc-api-key-user";
  const email = profile?.email ?? null;
  const vpcUser = {
    uid,
    email,
    emailVerified: true,
    displayName: email || "VPC operator",
    isAnonymous: false,
    photoURL: null,
    phoneNumber: null,
    providerId: "vpc-api-key",
    tenantId: null,
    metadata: {},
    providerData: [],
    refreshToken: "",
    getIdToken: async () => apiKey,
    getIdTokenResult: async () => ({
      token: apiKey,
      claims: {},
      authTime: "",
      issuedAtTime: "",
      expirationTime: "",
      signInProvider: "vpc-api-key",
      signInSecondFactor: null,
    }),
    reload: async () => undefined,
    delete: async () => undefined,
    toJSON: () => ({ uid, email }),
  };
  return vpcUser as unknown as User;
};

const fetchLoginAccountState = async (email: string): Promise<LoginAccountState> => {
  const response = await fetch(`${API_BASE_URL}/users/account-state`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch login account state (${response.status})`);
  }

  return await response.json();
};

export const AuthProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const cancelledRef = useRef(false);
  const initialAuthCheckCompletedRef = useRef(false);
  const invalidationInFlightRef = useRef(false);
  const validationInFlightRef = useRef(false);
  const initialValidationRetryRef = useRef<number | null>(null);
  const verifiedIdentityRef = useRef(false);
  const validationTimerRef = useRef<number | null>(null);

  const clearValidationTimer = useCallback(() => {
    if (validationTimerRef.current !== null) {
      window.clearInterval(validationTimerRef.current);
      validationTimerRef.current = null;
    }
  }, []);

  const invalidateSession = useCallback(
    async (reason: "password-expired" | "session-expired" | "account-state-check-failed") => {
      if (invalidationInFlightRef.current) return;
      invalidationInFlightRef.current = true;
      clearValidationTimer();
      await signOutQuietly();
      Sentry.setUser(null);

      if (!cancelledRef.current) {
        setUser(null);
        setLoading(false);
      }

      invalidationInFlightRef.current = false;

      if (reason === "password-expired") {
        redirectToLoginForPasswordReset();
      } else if (reason === "session-expired") {
        redirectToLoginForSessionExpiry();
      } else {
        redirectToLoginForAccountStateCheckFailure();
      }
    },
    [clearValidationTimer],
  );

  const validateSession = useCallback(
    async (
      firebaseUser: User,
      options: { strict?: boolean } = {},
    ): Promise<boolean> => {
      if (validationInFlightRef.current || invalidationInFlightRef.current) {
        return false;
      }

      validationInFlightRef.current = true;

      try {
        const profile = await fetchVerifiedProfile(firebaseUser, fetchCurrentUserProfile);

        if (profile.password_reset_required) {
          await invalidateSession("password-expired");
          return false;
        }

        if (!cancelledRef.current) {
          setUser(firebaseUser);
          verifiedIdentityRef.current = true;
          setLoading(false);
        }

        return true;
      } catch (error) {
        if (isSessionInvalidError(error)) {
          await invalidateSession("session-expired");
          return false;
        }

        if (options.strict) {
          console.error("Error checking account state:", error);
        }
        // A token refresh is not evidence of revocation when the backend is
        // restarting or unavailable. Keep established UI identity and the
        // validation timer; initial sessions remain unverified until a check succeeds.
        console.warn("Account check unavailable; retaining session and retrying:", error);
        return false;
      } finally {
        validationInFlightRef.current = false;
      }
    },
    [invalidateSession, isSessionInvalidError],
  );

  const validateAuthenticatedUser = useCallback(
    async (firebaseUser: User) => {
      try {
        const profile = await fetchVerifiedProfile(firebaseUser, fetchCurrentUserProfile);

        if (profile.password_reset_required) {
          await signOutQuietly();
          throw buildAuthError("auth/password-expired", PASSWORD_EXPIRED_MESSAGE);
        }
      } catch (error) {
        if ((error as AuthError).code === "auth/password-expired") {
          throw error;
        }

        if (isSessionInvalidError(error)) {
          await signOutQuietly();
          throw buildAuthError("auth/session-expired", SESSION_EXPIRED_MESSAGE);
        }

        await signOutQuietly();
        throw buildAuthError(
          "auth/account-state-check-failed",
          ACCOUNT_STATE_CHECK_FAILED_MESSAGE,
        );
      }
    },
    [isSessionInvalidError],
  );

  const validateCurrentSession = useCallback(
    async (options: { strict?: boolean } = {}) => {
      const currentUser = auth.currentUser;
      if (!currentUser) return false;
      return await validateSession(currentUser, options);
    },
    [validateSession],
  );

  useEffect(() => {
    cancelledRef.current = false;

    if (hasVpcApiKey()) {
      // VPC mode: no Firebase session to listen to. The api key is the
      // credential, and it can still expire or be revoked -- bootstrap issues
      // 30-day keys and rotates them -- so it is re-checked on the same
      // schedule a Firebase session would be: periodically and on focus.
      const apiKey = getVpcApiKey() as string;
      // Revalidation runs concurrently with the start-up profile request, so a
      // 401 can arrive first and a slower success afterwards. Anything that
      // resolves after a sign-out belongs to a dead generation and is dropped.
      let vpcGeneration = 0;
      const signOutVpc = () => {
        vpcGeneration += 1;
        clearVpcApiKey();
        if (!cancelledRef.current) {
          setUser(null);
          setLoading(false);
        }
      };
      void (async () => {
        const generation = vpcGeneration;
        let profile: CurrentUserProfile | null = null;
        try {
          profile = await fetchCurrentUserProfile(apiKey);
        } catch (error) {
          const status = (error as AuthError | undefined)?.status;
          if (status === 401 || status === 403) {
            // The stored key is invalid or revoked: do not present a signed-in
            // shell over 401s. Forget the key; the route guards send us to /login.
            signOutVpc();
            return;
          }
          console.warn("VPC mode: /users/me unavailable, continuing with a placeholder user:", error);
        }
        if (cancelledRef.current || generation !== vpcGeneration) return;
        const vpcUser = buildVpcUser(apiKey, profile);
        Sentry.setUser({ uid: vpcUser.uid, email: vpcUser.email || undefined });
        setUser(vpcUser);
        setLoading(false);
      })();

      // A key revoked after start-up would otherwise leave a signed-in shell
      // whose every request fails. Re-check quietly; only 401/403 signs out.
      const revalidate = () => {
        if (document.visibilityState !== "visible") return;
        const current = getVpcApiKey();
        if (!current) return signOutVpc();
        const generation = vpcGeneration;
        void fetchCurrentUserProfile(current).catch((error) => {
          if (generation !== vpcGeneration) return;   // a newer check already decided
          const status = (error as AuthError | undefined)?.status;
          if (status === 401 || status === 403) signOutVpc();
        });
      };
      const vpcTimer = window.setInterval(revalidate, SESSION_VALIDATION_INTERVAL_MS);
      window.addEventListener("focus", revalidate);
      document.addEventListener("visibilitychange", revalidate);

      return () => {
        cancelledRef.current = true;
        window.clearInterval(vpcTimer);
        window.removeEventListener("focus", revalidate);
        document.removeEventListener("visibilitychange", revalidate);
      };
    }

    const verifyInitialSession = async (currentUser: User): Promise<boolean> => {
      const verified = await validateSession(currentUser, { strict: true });
      if (!verified && !verifiedIdentityRef.current && !cancelledRef.current && auth.currentUser === currentUser) {
        // Keep route guards waiting while account verification is unavailable.
        // Retry promptly instead of redirecting a persisted identity to login.
        setLoading(true);
        if (initialValidationRetryRef.current === null) {
          initialValidationRetryRef.current = window.setTimeout(() => {
            initialValidationRetryRef.current = null;
            if (!cancelledRef.current && auth.currentUser === currentUser) void verifyInitialSession(currentUser);
          }, 3000);
        }
      }
      return verified;
    };

    const unsubscribe = onIdTokenChanged(auth, (currentUser) => {
      void (async () => {
        if (currentUser) {
          console.log("User is logged in:", currentUser.uid);
          Sentry.setUser({
            uid: currentUser.uid,
            email: currentUser.email || undefined,
          });
          const verified = await verifyInitialSession(currentUser);
          if (!verified && !verifiedIdentityRef.current && auth.currentUser) return;
        } else {
          verifiedIdentityRef.current = false;
          console.log("No user is logged in.");
          Sentry.setUser(null);
          clearValidationTimer();
          if (!cancelledRef.current) {
            setUser(null);
          }
        }

        if (!initialAuthCheckCompletedRef.current) {
          const finishInitialLoading = () => {
            if (!cancelledRef.current) {
              setLoading(false);
            }
          };
          // Account validation above stays mandatory. Workbench can start its
          // authorized bootstrap immediately once that check has finished.
          const delay = initialAuthLoadingDelay(window.location.pathname);
          if (delay === 0) finishInitialLoading();
          else window.setTimeout(finishInitialLoading, delay);
          initialAuthCheckCompletedRef.current = true;
        } else if (!cancelledRef.current) {
          setLoading(false);
        }
      })();
    });

    const runSessionValidation = () => {
      if (document.visibilityState !== "visible") return;
      void validateCurrentSession({ strict: false });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runSessionValidation();
      }
    };

    validationTimerRef.current = window.setInterval(
      runSessionValidation,
      SESSION_VALIDATION_INTERVAL_MS,
    );
    window.addEventListener("focus", runSessionValidation);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelledRef.current = true;
      clearValidationTimer();
      window.removeEventListener("focus", runSessionValidation);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (initialValidationRetryRef.current !== null) {
        window.clearTimeout(initialValidationRetryRef.current);
        initialValidationRetryRef.current = null;
      }
      unsubscribe();
    };
  }, [clearValidationTimer, validateCurrentSession, validateSession]);

  const login = async (email: string, password: string) => {
    if (isVpcMode()) {
      // No Firebase in a VPC deployment: the login form trades the password
      // for an API key (POST /bootstrap/login), which is then the identity.
      // Reload afterwards so main.tsx installs the X-API-Key fetch patch and
      // the effect above builds the VPC user from /users/me.
      const res = await fetch(`${API_BASE_URL}/bootstrap/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null))?.detail;
        throw buildAuthError(
          "auth/invalid-credential",
          typeof detail === "string" ? detail : "Invalid email or password",
        );
      }
      const data = (await res.json()) as { key: string };
      setVpcApiKey(data.key);
      // Same-origin only: "//evil.example" and "/\\evil.example" both start
      // with "/" but are protocol-relative URLs the browser resolves off-site.
      const requested = new URLSearchParams(window.location.search).get("returnTo") || "";
      const returnTo =
        requested.startsWith("/") && !requested.startsWith("//") && !requested.startsWith("/\\")
          ? requested
          : "/";
      window.location.assign(returnTo);
      return new Promise<never>(() => undefined); // page is unloading
    }
    let userCredential: UserCredential;
    try {
      userCredential = await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      const authError = error as AuthError;
      if (
        authError.code === "auth/user-not-found" ||
        authError.code === "auth/wrong-password" ||
        authError.code === "auth/invalid-credential"
      ) {
        try {
          const accountState = await fetchLoginAccountState(email);
          if (accountState.password_reset_required) {
            const passwordExpiredError = new Error(PASSWORD_EXPIRED_MESSAGE) as AuthError;
            passwordExpiredError.code = "auth/password-expired";
            throw passwordExpiredError;
          }
        } catch (accountStateError) {
          if ((accountStateError as AuthError).code === "auth/password-expired") {
            throw accountStateError;
          }
          console.warn("Unable to fetch login account state:", accountStateError);
        }
      }
      throw error;
    }

    if (!userCredential.user.emailVerified) {
      await signOut(auth);
      throw new Error("Please verify your email before logging in.");
    }

    await validateAuthenticatedUser(userCredential.user);
    return userCredential;
  };

  const logout = async () => {
    clearAuthNotices();
    clearValidationTimer();
    if (isVpcMode()) {
      clearVpcApiKey();
      window.location.assign("/login");
      return;
    }
    return await signOut(auth);
  };

  const signup = async (
    email: string,
    password: string,
    verificationReturnTo?: string,
  ) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const actionCodeSettings =
      verificationReturnTo &&
      verificationReturnTo.startsWith("/") &&
      !verificationReturnTo.startsWith("//")
        ? {
            url: new URL(
              verificationReturnTo,
              window.location.origin,
            ).toString(),
          }
        : undefined;
    await sendEmailVerification(userCredential.user, actionCodeSettings);
    return userCredential;
  };

  const sendVerificationEmail = async () => {
    if (user) {
      await sendEmailVerification(user);
    } else {
      throw new Error("No user is signed in");
    }
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const getToken = useCallback(async (refresh: boolean = false): Promise<string | null> => {
    // VPC mode: the api key is the token (see buildVpcUser).
    if (hasVpcApiKey()) return getVpcApiKey();
    if (!auth.currentUser) return null;

    try {
      return await auth.currentUser.getIdToken(refresh);
    } catch (error) {
      console.error("Error getting ID token:", error);
      if (isSessionInvalidError(error)) {
        await invalidateSession("session-expired");
      }
      return null;
    }
  }, [invalidateSession, isSessionInvalidError]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        signup,
        sendVerificationEmail,
        resetPassword,
        getToken,
        validateAuthenticatedUser,
      }}
    >
      {loading ? <AppLoadingScreen /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
